import React from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Sparklines, SparklinesLine, SparklinesCurve } from 'react-sparklines';
interface KPICardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  color: string;
  trend?: {
    value: number;
    label: string;
    data: number[];
  };
  /** 'standard' = green up / red down; 'pending' = red up / yellow down / green if 0 */
  trendColorMode?: 'standard' | 'pending';
  borderColor?: string;
}
const KPICard: React.FC<KPICardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  trendColorMode = 'standard',
  borderColor = '#E5E7EB'
}) => {
  const getTrendColor = () => {
    if (!trend) return '#0B7923';
    if (trendColorMode === 'pending') {
      return '#B38600'; // always yellow for pending
    }
    // standard: green up, red down
    return trend.value >= 0 ? '#0B7923' : '#AE2A19';
  };
  const trendColor = getTrendColor();
  return <Card sx={{
    p: 0,
    borderRadius: '8px',
    border: `1px solid ${borderColor}`,
    boxShadow: '0px 1px 2px rgba(0,0,0,0.05)',
    transition: 'all 200ms ease',
    height: '100%',
    overflow: 'hidden',
    position: 'relative',
    background: '#FFFFFF',
    '&:hover': {
      boxShadow: `0px 4px 12px rgba(0,0,0,0.08)`
    }
  }} data-id="element-1093">
      <Box sx={{
      p: 2,
      pt: 2
    }} data-id="element-1094">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        mb: trend ? 1 : 0
      }} data-id="element-1095">
          <Box sx={{
          minWidth: 0,
          flex: 1
        }} data-id="element-1096">
            <Typography variant="body2" sx={{
            color: '#6B7280',
            fontSize: '0.75rem',
            fontWeight: 600,
            mb: 0.3,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            lineHeight: 1.3,
            minHeight: '2.2em',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }} data-id="element-1097">
              {title}
            </Typography>
            <Typography variant="h5" sx={{
            fontWeight: 700,
            color: '#1F2937',
            fontSize: '1.35rem',
            lineHeight: 1.2,
            letterSpacing: '-0.02em'
          }} data-id="element-1098">
              {value}
            </Typography>
          </Box>
          <Box sx={{
          width: 34,
          height: 34,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: `${color}1A`,
          color: color,
          flexShrink: 0,
          ml: 1
        }} data-id="element-1099">
            {icon}
          </Box>
        </Box>

        {trend && <Box data-id="element-1100">
            <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mb: 0.5
        }} data-id="element-1101">
              <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.3,
            bgcolor: `${trendColor}10`,
            borderRadius: '6px',
            px: 0.8,
            py: 0.2
          }} data-id="element-1102">
                <Typography variant="caption" sx={{
              color: trendColor,
              fontWeight: 700,
              fontSize: '0.65rem',
              display: 'flex',
              alignItems: 'center',
              gap: 0.2,
              lineHeight: 1.4
            }} data-id="element-1103">
                  {trend.value > 0 ? '▲' : trend.value < 0 ? '▼' : '—'}{' '}
                  {Math.abs(trend.value)}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{
          height: 26,
          width: '100%',
          opacity: 0.85
        }} data-id="element-1104">
              <Sparklines data={trend.data} width={120} height={26} margin={2} data-id="element-1105">
                <SparklinesLine color={trendColor} style={{
              strokeWidth: 1.8,
              fill: 'none'
            }} data-id="element-1106" />
              </Sparklines>
            </Box>
          </Box>}
      </Box>
    </Card>;
};
export default KPICard;